/**
 * Travel Assistant Overview Hook
 *
 * Fetches overview data and caches it in React Query cache by (location, sessionId, userId).
 * Use getCachedOverview(request) to read from cache before fetching for instant display on reopen.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchOverview } from '@/services/travelAssistantService';
import type {
  OverviewRequest,
  OverviewResponse,
} from '@/services/api/interfaces/ITravelAssistantRepository';

const OVERVIEW_QUERY_KEY_PREFIX = ['travelAssistant', 'overview'] as const;

function overviewCacheKey(request: OverviewRequest): unknown[] {
  return [
    ...OVERVIEW_QUERY_KEY_PREFIX,
    String(request?.location ?? '').trim(),
    String(request?.session_id ?? '').trim(),
    String(request?.user_id ?? '').trim(),
  ];
}

export interface UseTravelAssistantOverviewOptions {
  accessToken?: string;
  refreshToken?: string;
}

export interface UseTravelAssistantOverviewResult {
  overview: OverviewResponse | null;
  isLoading: boolean;
  error: Error | null;
  fetchOverview: (
    request: OverviewRequest,
    signal?: AbortSignal
  ) => Promise<OverviewResponse | null>;
  clearError: () => void;
  /** Returns cached overview for this request if available (for instant display on reopen). */
  getCachedOverview: (request: OverviewRequest) => OverviewResponse | null;
}

/**
 * Hook for fetching travel assistant overview with React Query cache.
 * Successful fetches are cached; use getCachedOverview() to serve cached data when reopening the same place.
 */
export function useTravelAssistantOverview(
  options?: UseTravelAssistantOverviewOptions
): UseTravelAssistantOverviewResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      request,
      signal,
    }: {
      request: OverviewRequest;
      signal?: AbortSignal;
    }) => {
      const result = await fetchOverview(
        request,
        options?.accessToken,
        options?.refreshToken,
        signal
      );
      return result;
    },
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueryData(overviewCacheKey(variables.request), data);
      }
    },
    onError: error => {
      console.error('❌ [OVERVIEW HOOK] onError called:', error);
    },
    retry: (failureCount, error) => {
      // Don't retry if the request was canceled
      if (
        (error as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (error as Error & { name?: string })?.name === 'CanceledError' ||
        (error as Error).message === 'canceled'
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Manual fetch function that uses the mutation
  const fetchOverviewData = async (
    request: OverviewRequest,
    signal?: AbortSignal
  ): Promise<OverviewResponse | null> => {
    try {
      const response = await mutation.mutateAsync({ request, signal });
      return response;
    } catch (err) {
      // Ignore cancellation errors
      if (
        (err as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (err as Error & { name?: string })?.name === 'CanceledError' ||
        (err as Error).message === 'canceled'
      ) {
        return null;
      }

      const error =
        err instanceof Error ? err : new Error('Failed to fetch overview');
      console.error('Error fetching overview:', error);
      return null;
    }
  };

  const clearError = () => {
    mutation.reset();
  };

  const getCachedOverview = (
    request: OverviewRequest
  ): OverviewResponse | null => {
    return (
      queryClient.getQueryData<OverviewResponse>(overviewCacheKey(request)) ??
      null
    );
  };

  return {
    overview: mutation.data || null,
    isLoading: mutation.isPending,
    error: mutation.error as Error | null,
    fetchOverview: fetchOverviewData,
    clearError,
    getCachedOverview,
  };
}
